import type * as React from 'react';

import { OrganizationFeature, type Organization } from '@gitbook/api-client';
import { isPricingFeatureAvailable } from '@gitbook/app-pricing';

import { AISearchAnswer, type AISearchQueryState } from '../../AISearch';
import { useRegisterRecentSearch, useSearchQueryAnalyticsEvent } from '../../Search';
import { UpgradeDialogProvider } from '../../UpgradeDialog';
import { AppSearchBlankslate } from './AppSearchBlankslate';
import { AppSearchResults } from './AppSearchResults';

/**
 * Body of the search/ask-gitbook dialog.
 */
export function AppSearchBody(props: {
    organization: Organization | null;
    query: string;
    searchQuery: string;
    aiQuery: AISearchQueryState;
    onUpdateQuery: (query: string) => void;
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void;
    usingAI: boolean;
}): React.ReactElement {
    const { organization, query, searchQuery, aiQuery, onUpdateQuery, onSubmitAIQuery, usingAI } =
        props;

    useRegisterRecentSearch(query);
    useSearchQueryAnalyticsEvent(query, { organization });

    if (
        organization &&
        usingAI &&
        isPricingFeatureAvailable(organization, { featureKey: OrganizationFeature.AiLens })
    ) {
        return (
            <AISearchAnswer
                target={{ organization: organization.id }}
                query={aiQuery}
                onSubmitQuery={onSubmitAIQuery}
            />
        );
    }

    if (!query) {
        return (
            <AppSearchBlankslate
                organization={organization}
                onUpdateQuery={onUpdateQuery}
                onSubmitAIQuery={onSubmitAIQuery}
            />
        );
    }

    if (organization) {
        // we need the organization to determine whether AI is enabled so only render the AI version
        // if we have an organization
        return (
            <UpgradeDialogProvider>
                <AppSearchResultsWithAI
                    query={query}
                    searchQuery={searchQuery}
                    onSubmitAIQuery={onSubmitAIQuery}
                    organization={organization}
                />
            </UpgradeDialogProvider>
        );
    }

    return (
        <AppSearchResults
            query={query}
            searchQuery={searchQuery}
            onSubmitAIQuery={onSubmitAIQuery}
            organization={null}
        />
    );
}

function AppSearchResultsWithAI({
    query,
    searchQuery,
    onSubmitAIQuery,
    organization,
}: {
    query: string;
    searchQuery: string;
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void;
    organization: Organization;
}) {
    return (
        <AppSearchResults
            query={query}
            searchQuery={searchQuery}
            onSubmitAIQuery={onSubmitAIQuery}
            hasAI={isPricingFeatureAvailable(organization, {
                featureKey: OrganizationFeature.AiLens,
            })}
            organization={organization.id}
        />
    );
}
